/**
 * Settings View
 * Configure system parameters, API keys, and preferences
 * MoM (Mixture of Minds) router LLM configuration
 */

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Key,
  Cpu,
  Database,
  Bell,
  Shield,
  Palette,
  Save,
  RotateCcw,
  Eye,
  EyeOff,
  Check,
  AlertTriangle,
  Loader2,
  X,
  Brain,
  Zap,
  Code,
  MessageSquare,
} from "lucide-react";
import * as Tabs from "@radix-ui/react-tabs";
import { cn } from "@/lib/utils";
import { useDashboardStore } from "@/store";
import { convexService } from "@/services/convexService";

const SETTINGS_TABS = [
  { id: "api", label: "API Keys", icon: Key },
  { id: "mom", label: "MoM Router", icon: Brain },
  { id: "runtime", label: "Runtime", icon: Cpu },
  { id: "database", label: "Database", icon: Database },
  { id: "security", label: "Security", icon: Shield },
  { id: "appearance", label: "Appearance", icon: Palette },
];

export function SettingsView() {
  const { settings, updateSettings, addLogEntry } = useDashboardStore();
  const [activeTab, setActiveTab] = useState("api");
  const [saveMessage, setSaveMessage] = useState<{ type: "success" | "error"; message: string } | null>(null);

  const handleSave = (section: string) => {
    addLogEntry({
      category: "settings",
      action: `${section} Settings Saved`,
      details: `${section} configuration updated`,
      severity: "info",
      source: "settings-view",
    });
    setSaveMessage({ type: "success", message: "Settings saved successfully" });
    setTimeout(() => setSaveMessage(null), 3000);
  };

  return (
    <div className="space-y-6">
      <Tabs.Root value={activeTab} onValueChange={setActiveTab}>
        <Tabs.List className="flex gap-1 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-1">
          {SETTINGS_TABS.map((tab) => (
            <Tabs.Trigger
              key={tab.id}
              value={tab.id}
              className={cn(
                "flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors cursor-pointer",
                activeTab === tab.id
                  ? "bg-[hsl(var(--primary))] text-black"
                  : "text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]"
              )}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </Tabs.Trigger>
          ))}
        </Tabs.List>

        <div className="mt-6">
          <Tabs.Content value="api">
            <APIKeysSettings onSave={() => handleSave("API Keys")} />
          </Tabs.Content>
          <Tabs.Content value="mom">
            <MoMSettings onSave={() => handleSave("MoM Router")} />
          </Tabs.Content>
          <Tabs.Content value="runtime">
            <RuntimeSettings onSave={() => handleSave("Runtime")} />
          </Tabs.Content>
          <Tabs.Content value="database">
            <DatabaseSettings onSave={() => handleSave("Database")} />
          </Tabs.Content>
          <Tabs.Content value="security">
            <SecuritySettings onSave={() => handleSave("Security")} />
          </Tabs.Content>
          <Tabs.Content value="appearance">
            <AppearanceSettings onSave={() => handleSave("Appearance")} />
          </Tabs.Content>
        </div>
      </Tabs.Root>

      {/* Save Message Toast */}
      <AnimatePresence>
        {saveMessage && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className={cn(
              "fixed bottom-6 right-6 rounded-lg p-4 shadow-lg",
              saveMessage.type === "success"
                ? "bg-green-500/20 border border-green-500/30 text-green-400"
                : "bg-red-500/20 border border-red-500/30 text-red-400"
            )}
          >
            <div className="flex items-center gap-2">
              {saveMessage.type === "success" ? (
                <Check className="h-4 w-4" />
              ) : (
                <X className="h-4 w-4" />
              )}
              {saveMessage.message}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function APIKeysSettings({ onSave }: { onSave: () => void }) {
  const { settings, updateSettings } = useDashboardStore();
  const [showKeys, setShowKeys] = useState<Record<string, boolean>>({});
  const [localSettings, setLocalSettings] = useState({
    glmApiKey: settings.glmApiKey,
    glmBaseUrl: settings.glmBaseUrl,
    glmModel: settings.glmModel,
    deepseekApiKey: settings.deepseekApiKey,
    deepseekBaseUrl: settings.deepseekBaseUrl,
    deepseekModel: settings.deepseekModel,
    qwenApiKey: settings.qwenApiKey,
    qwenBaseUrl: settings.qwenBaseUrl,
    qwenModel: settings.qwenModel,
  });

  const handleSave = () => {
    updateSettings(localSettings);
    onSave();
  };

  const handleReset = () => {
    setLocalSettings({
      glmApiKey: "",
      glmBaseUrl: "https://api.z.ai/api/coding/paas/v4",
      glmModel: "glm-4.7",
      deepseekApiKey: "",
      deepseekBaseUrl: "https://apis.iflow.cn/v1",
      deepseekModel: "deepseek-r1",
      qwenApiKey: "",
      qwenBaseUrl: "https://apis.iflow.cn/v1",
      qwenModel: "qwen3-coder-plus",
    });
  };

  return (
    <SettingsCard
      title="API Configuration"
      description="Configure your LLM provider API keys and endpoints"
      onSave={handleSave}
      onReset={handleReset}
    >
      <div className="space-y-6">
        {/* GLM Section */}
        <div>
          <h4 className="mb-3 flex items-center gap-2 text-sm font-medium">
            <MessageSquare className="h-4 w-4 text-blue-400" />
            GLM (z.ai) - General Orchestration
          </h4>
          <div className="space-y-3">
            <div className="flex items-center gap-4">
              <div className="w-32">
                <label className="text-sm text-[hsl(var(--muted-foreground))]">API Key</label>
              </div>
              <div className="relative flex-1">
                <input
                  type={showKeys["glm"] ? "text" : "password"}
                  placeholder="Enter GLM API Key"
                  value={localSettings.glmApiKey}
                  onChange={(e) => setLocalSettings({ ...localSettings, glmApiKey: e.target.value })}
                  className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted)/.5)] px-3 py-2 pr-10 text-sm font-mono"
                />
                <button
                  type="button"
                  onClick={() => setShowKeys((s) => ({ ...s, glm: !s.glm }))}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-white cursor-pointer"
                >
                  {showKeys["glm"] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-32">
                <label className="text-sm text-[hsl(var(--muted-foreground))]">Base URL</label>
              </div>
              <input
                type="text"
                value={localSettings.glmBaseUrl}
                onChange={(e) => setLocalSettings({ ...localSettings, glmBaseUrl: e.target.value })}
                className="flex-1 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted)/.5)] px-3 py-2 text-sm font-mono"
              />
            </div>
            <div className="flex items-center gap-4">
              <div className="w-32">
                <label className="text-sm text-[hsl(var(--muted-foreground))]">Model</label>
              </div>
              <input
                type="text"
                value={localSettings.glmModel}
                onChange={(e) => setLocalSettings({ ...localSettings, glmModel: e.target.value })}
                className="flex-1 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted)/.5)] px-3 py-2 text-sm font-mono"
              />
            </div>
          </div>
        </div>

        {/* DeepSeek Section */}
        <div>
          <h4 className="mb-3 flex items-center gap-2 text-sm font-medium">
            <Brain className="h-4 w-4 text-purple-400" />
            DeepSeek (iflow.cn) - Deep Reasoning
          </h4>
          <div className="space-y-3">
            <div className="flex items-center gap-4">
              <div className="w-32">
                <label className="text-sm text-[hsl(var(--muted-foreground))]">API Key</label>
              </div>
              <div className="relative flex-1">
                <input
                  type={showKeys["deepseek"] ? "text" : "password"}
                  placeholder="Enter DeepSeek API Key"
                  value={localSettings.deepseekApiKey}
                  onChange={(e) => setLocalSettings({ ...localSettings, deepseekApiKey: e.target.value })}
                  className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted)/.5)] px-3 py-2 pr-10 text-sm font-mono"
                />
                <button
                  type="button"
                  onClick={() => setShowKeys((s) => ({ ...s, deepseek: !s.deepseek }))}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-white cursor-pointer"
                >
                  {showKeys["deepseek"] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-32">
                <label className="text-sm text-[hsl(var(--muted-foreground))]">Base URL</label>
              </div>
              <input
                type="text"
                value={localSettings.deepseekBaseUrl}
                onChange={(e) => setLocalSettings({ ...localSettings, deepseekBaseUrl: e.target.value })}
                className="flex-1 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted)/.5)] px-3 py-2 text-sm font-mono"
              />
            </div>
            <div className="flex items-center gap-4">
              <div className="w-32">
                <label className="text-sm text-[hsl(var(--muted-foreground))]">Model</label>
              </div>
              <select
                value={localSettings.deepseekModel}
                onChange={(e) => setLocalSettings({ ...localSettings, deepseekModel: e.target.value })}
                className="flex-1 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted)/.5)] px-3 py-2 text-sm cursor-pointer"
              >
                <option value="deepseek-r1">deepseek-r1 (Reasoning)</option>
                <option value="deepseek-v3">deepseek-v3</option>
                <option value="deepseek-v3.2">deepseek-v3.2</option>
              </select>
            </div>
          </div>
        </div>

        {/* Qwen Section */}
        <div>
          <h4 className="mb-3 flex items-center gap-2 text-sm font-medium">
            <Code className="h-4 w-4 text-green-400" />
            Qwen (iflow.cn) - Code Generation
          </h4>
          <div className="space-y-3">
            <div className="flex items-center gap-4">
              <div className="w-32">
                <label className="text-sm text-[hsl(var(--muted-foreground))]">API Key</label>
              </div>
              <div className="relative flex-1">
                <input
                  type={showKeys["qwen"] ? "text" : "password"}
                  placeholder="Enter Qwen API Key"
                  value={localSettings.qwenApiKey}
                  onChange={(e) => setLocalSettings({ ...localSettings, qwenApiKey: e.target.value })}
                  className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted)/.5)] px-3 py-2 pr-10 text-sm font-mono"
                />
                <button
                  type="button"
                  onClick={() => setShowKeys((s) => ({ ...s, qwen: !s.qwen }))}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-white cursor-pointer"
                >
                  {showKeys["qwen"] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-32">
                <label className="text-sm text-[hsl(var(--muted-foreground))]">Base URL</label>
              </div>
              <input
                type="text"
                value={localSettings.qwenBaseUrl}
                onChange={(e) => setLocalSettings({ ...localSettings, qwenBaseUrl: e.target.value })}
                className="flex-1 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted)/.5)] px-3 py-2 text-sm font-mono"
              />
            </div>
            <div className="flex items-center gap-4">
              <div className="w-32">
                <label className="text-sm text-[hsl(var(--muted-foreground))]">Model</label>
              </div>
              <input
                type="text"
                value={localSettings.qwenModel}
                onChange={(e) => setLocalSettings({ ...localSettings, qwenModel: e.target.value })}
                className="flex-1 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted)/.5)] px-3 py-2 text-sm font-mono"
              />
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-yellow-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-yellow-400">MoM Routing Active</p>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">
                Mixture of Minds automatically routes tasks: GLM for orchestration, DeepSeek for reasoning, Qwen for coding.
              </p>
            </div>
          </div>
        </div>
      </div>
    </SettingsCard>
  );
}

function MoMSettings({ onSave }: { onSave: () => void }) {
  const { settings, updateSettings } = useDashboardStore();
  const [localSettings, setLocalSettings] = useState({
    enableFallbackModels: settings.enableFallbackModels,
    parallelModelQueries: settings.parallelModelQueries,
  });

  const handleSave = () => {
    updateSettings(localSettings);
    onSave();
  };

  // MoM Router Keywords
  const TASK_KEYWORDS = {
    reasoning: ["prove", "calculate", "analyze", "deduce", "verify", "logical", "math"],
    coding: ["implement", "refactor", "debug", "patch", "code", "function", "class", "write"],
    general: [], // Default for everything else
  };

  return (
    <SettingsCard
      title="Mixture of Minds (MoM) Router"
      description="Configure AI model routing for optimal task handling"
      onSave={handleSave}
    >
      <div className="space-y-6">
        {/* Model Descriptions */}
        <div className="grid grid-cols-3 gap-4">
          <div className="rounded-lg border border-blue-500/30 bg-blue-500/10 p-4">
            <div className="flex items-center gap-2 mb-2">
              <MessageSquare className="h-4 w-4 text-blue-400" />
              <h5 className="font-semibold text-blue-400">GLM-4.7</h5>
            </div>
            <p className="text-xs text-[hsl(var(--muted-foreground))]">
              General orchestration and agentic tasks
            </p>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
              Default router
            </p>
          </div>

          <div className="rounded-lg border border-purple-500/30 bg-purple-500/10 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Brain className="h-4 w-4 text-purple-400" />
              <h5 className="font-semibold text-purple-400">DeepSeek-R1</h5>
            </div>
            <p className="text-xs text-[hsl(var(--muted-foreground))]">
              Logic, math, proofs, reasoning
            </p>
            <div className="mt-2 flex flex-wrap gap-1">
              {TASK_KEYWORDS.reasoning.slice(0, 3).map((k) => (
                <span key={k} className="rounded bg-purple-500/20 px-1.5 py-0.5 text-xs text-purple-300">
                  {k}
                </span>
              ))}
              <span className="text-xs text-purple-300">...</span>
            </div>
          </div>

          <div className="rounded-lg border border-green-500/30 bg-green-500/10 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Code className="h-4 w-4 text-green-400" />
              <h5 className="font-semibold text-green-400">Qwen-Coder</h5>
            </div>
            <p className="text-xs text-[hsl(var(--muted-foreground))]">
              Code generation and patching
            </p>
            <div className="mt-2 flex flex-wrap gap-1">
              {TASK_KEYWORDS.coding.slice(0, 3).map((k) => (
                <span key={k} className="rounded bg-green-500/20 px-1.5 py-0.5 text-xs text-green-300">
                  {k}
                </span>
              ))}
              <span className="text-xs text-green-300">...</span>
            </div>
          </div>
        </div>

        {/* Router Settings */}
        <div className="space-y-4">
          <ToggleSetting
            label="Enable Fallback Models"
            description="Automatically fallback to alternative models on failure"
            checked={localSettings.enableFallbackModels}
            onChange={(checked) => setLocalSettings({ ...localSettings, enableFallbackModels: checked })}
          />

          <ToggleSetting
            label="Parallel Model Queries"
            description="Query multiple models simultaneously and select best response"
            checked={localSettings.parallelModelQueries}
            onChange={(checked) => setLocalSettings({ ...localSettings, parallelModelQueries: checked })}
          />
        </div>

        {/* Routing Information */}
        <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted)/.5)] p-4">
          <h4 className="font-medium text-sm mb-2">Routing Logic</h4>
          <ul className="space-y-2 text-sm text-[hsl(var(--muted-foreground))]">
            <li className="flex items-start gap-2">
              <Zap className="h-4 w-4 text-blue-400 mt-0.5" />
              <span>Prompt contains reasoning keywords → DeepSeek-R1</span>
            </li>
            <li className="flex items-start gap-2">
              <Zap className="h-4 w-4 text-green-400 mt-0.5" />
              <span>Prompt contains coding keywords → Qwen-Coder-Plus</span>
            </li>
            <li className="flex items-start gap-2">
              <Zap className="h-4 w-4 text-blue-400 mt-0.5" />
              <span>No specific keywords → GLM-4.7 (default)</span>
            </li>
          </ul>
        </div>
      </div>
    </SettingsCard>
  );
}

function RuntimeSettings({ onSave }: { onSave: () => void }) {
  const { settings, updateSettings } = useDashboardStore();
  const [localSettings, setLocalSettings] = useState({
    maxConcurrentRequests: settings.maxConcurrentRequests,
    requestTimeout: settings.requestTimeout,
    patternCacheSize: settings.patternCacheSize,
    enablePatternBypass: settings.enablePatternBypass,
  });

  const handleSave = () => {
    updateSettings(localSettings);
    onSave();
  };

  return (
    <SettingsCard
      title="Runtime Configuration"
      description="Configure system runtime behavior and performance"
      onSave={handleSave}
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-[hsl(var(--muted-foreground))]">Max Concurrent Requests</label>
            <input
              type="number"
              value={localSettings.maxConcurrentRequests}
              onChange={(e) => setLocalSettings({ ...localSettings, maxConcurrentRequests: parseInt(e.target.value) || 5 })}
              min="1"
              className="mt-1 w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted)/.5)] px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-sm text-[hsl(var(--muted-foreground))]">Request Timeout (ms)</label>
            <input
              type="number"
              value={localSettings.requestTimeout}
              onChange={(e) => setLocalSettings({ ...localSettings, requestTimeout: parseInt(e.target.value) || 30000 })}
              min="1000"
              step="1000"
              className="mt-1 w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted)/.5)] px-3 py-2 text-sm"
            />
          </div>
        </div>

        <ToggleSetting
          label="Enable Pattern Bypass"
          description="Allow cached patterns to bypass LLM calls"
          checked={localSettings.enablePatternBypass}
          onChange={(checked) => setLocalSettings({ ...localSettings, enablePatternBypass: checked })}
        />
      </div>
    </SettingsCard>
  );
}

function DatabaseSettings({ onSave }: { onSave: () => void }) {
  const { settings, updateSettings } = useDashboardStore();
  const [localSettings, setLocalSettings] = useState({
    convexUrl: settings.convexUrl,
  });
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<boolean | null>(null);

  const testConnection = async () => {
    setIsTesting(true);
    setTestResult(null);

    // Update convex service
    convexService.initialize(localSettings.convexUrl);

    try {
      const success = await convexService.healthCheck();
      setTestResult(success);
      if (success) {
        updateSettings({ convexUrl: localSettings.convexUrl });
        onSave();
      }
    } catch {
      setTestResult(false);
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <SettingsCard
      title="Database Configuration"
      description="Configure Convex database connection"
      onSave={() => {
        updateSettings(localSettings);
        onSave();
      }}
    >
      <div className="space-y-4">
        <div>
          <label className="text-sm text-[hsl(var(--muted-foreground))]">Convex Deployment URL</label>
          <div className="mt-1 flex gap-2">
            <input
              type="text"
              placeholder="http://127.0.0.1:3210"
              value={localSettings.convexUrl}
              onChange={(e) => {
                setLocalSettings({ ...localSettings, convexUrl: e.target.value });
                setTestResult(null);
              }}
              className="flex-1 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted)/.5)] px-3 py-2 text-sm font-mono"
            />
            <button
              type="button"
              onClick={testConnection}
              disabled={isTesting || !localSettings.convexUrl}
              className={cn(
                "rounded-lg px-4 py-2 text-sm font-medium flex items-center gap-2 cursor-pointer transition-colors",
                isTesting || !localSettings.convexUrl
                  ? "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] cursor-not-allowed"
                  : "bg-[hsl(var(--primary))] text-black hover:bg-[hsl(var(--primary)/.9)]"
              )}
            >
              {isTesting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Testing...
                </>
              ) : (
                "Test"
              )}
            </button>
          </div>
          {testResult !== null && (
            <div className={cn(
              "mt-2 flex items-center gap-2 text-sm",
              testResult ? "text-green-400" : "text-red-400"
            )}>
              {testResult ? (
                <>
                  <Check className="h-4 w-4" />
                  Connection successful
                </>
              ) : (
                <>
                  <X className="h-4 w-4" />
                  Connection failed
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </SettingsCard>
  );
}

function SecuritySettings({ onSave }: { onSave: () => void }) {
  const { settings, updateSettings } = useDashboardStore();
  const [localSettings, setLocalSettings] = useState({
    sessionTimeout: settings.sessionTimeout,
    requireReauthAfter: settings.requireReauthAfter,
  });

  const handleSave = () => {
    updateSettings(localSettings);
    onSave();
  };

  return (
    <SettingsCard
      title="Security Settings"
      description="Configure authentication and session management"
      onSave={handleSave}
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-[hsl(var(--muted-foreground))]">Session Timeout (hours)</label>
            <input
              type="number"
              value={localSettings.sessionTimeout}
              onChange={(e) => setLocalSettings({ ...localSettings, sessionTimeout: parseInt(e.target.value) || 4 })}
              min="1"
              max="24"
              className="mt-1 w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted)/.5)] px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-sm text-[hsl(var(--muted-foreground))]">Require Re-auth (hours)</label>
            <input
              type="number"
              value={localSettings.requireReauthAfter}
              onChange={(e) => setLocalSettings({ ...localSettings, requireReauthAfter: parseInt(e.target.value) || 12 })}
              min="1"
              max="168"
              className="mt-1 w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted)/.5)] px-3 py-2 text-sm"
            />
          </div>
        </div>
      </div>
    </SettingsCard>
  );
}

function AppearanceSettings({ onSave }: { onSave: () => void }) {
  const { settings, updateSettings, theme } = useDashboardStore();

  return (
    <SettingsCard
      title="Appearance"
      description="Customize the control center look and feel"
      onSave={onSave}
    >
      <div className="space-y-4">
        <div>
          <label className="text-sm text-[hsl(var(--muted-foreground))] mb-2 block">Theme</label>
          <div className="flex gap-2">
            {(["dark", "light", "system"] as const).map((t) => (
              <button
                key={t}
                onClick={() => {
                  const newTheme = t === "system" ? window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light" : t;
                  updateSettings({ theme: t });
                  document.documentElement.setAttribute("data-theme", newTheme);
                  onSave();
                }}
                className={cn(
                  "flex-1 rounded-lg border px-4 py-2 text-sm capitalize cursor-pointer transition-colors",
                  theme === t
                    ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary)/.2)] text-[hsl(var(--primary))]"
                    : "border-[hsl(var(--border))] bg-[hsl(var(--muted)/.5)] text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]"
                )}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      </div>
    </SettingsCard>
  );
}

function SettingsCard({
  title,
  description,
  children,
  onSave,
  onReset,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
  onSave: () => void;
  onReset?: () => void;
}) {
  return (
    <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold">{title}</h3>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">{description}</p>
        </div>
        <div className="flex gap-2">
          {onReset && (
            <button
              onClick={onReset}
              className="flex items-center gap-2 rounded-lg border border-[hsl(var(--border))] px-3 py-2 text-sm hover:bg-[hsl(var(--muted))] cursor-pointer"
            >
              <RotateCcw className="h-4 w-4" />
              Reset
            </button>
          )}
          <button
            onClick={onSave}
            className="flex items-center gap-2 rounded-lg bg-[hsl(var(--primary))] px-3 py-2 text-sm font-medium text-black hover:bg-[hsl(var(--primary)/.9)] cursor-pointer"
          >
            <Save className="h-4 w-4" />
            Save
          </button>
        </div>
      </div>
      {children}
    </div>
  );
}

function ToggleSetting({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted)/.5)] p-4">
      <div className="flex-1">
        <label className="text-sm font-medium">{label}</label>
        <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">{description}</p>
      </div>
      <button
        onClick={() => onChange(!checked)}
        className={cn(
          "h-6 w-11 rounded-full transition-colors cursor-pointer relative",
          checked ? "bg-[hsl(var(--primary))]" : "bg-[hsl(var(--muted))]"
        )}
      >
        <motion.div
          animate={{ x: checked ? 20 : 4 }}
          className="absolute top-0.5 h-5 w-5 rounded-full bg-white shadow"
        />
      </button>
    </div>
  );
}
